/**
 * The legend under a drawing's Legend toggle: an entry per kind (its picture and name; the count on
 * hover). Every hero drawing shares it, with the toggle in the same place.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleButton } from '@bn-apps/dim-ui-react';
import { Glyph } from './alongTrack';
import styles from './legend.module.scss';

export interface LegendEntry {
  key: string;
  icon?: ReactNode;
  label: string;
  count?: number;
}

/** Shows or hides a drawing's legend; the same button on every hero. */
export function LegendToggle({ open, onToggle }: { open: boolean; onToggle: (open: boolean) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <ToggleButton size="xs" variant="tertiary" isSelected={open} onChange={onToggle}>
      {t('entity.template.along.legend')}
    </ToggleButton>
  );
}

export function Legend({ label, entries }: { label: string; entries: LegendEntry[] }): JSX.Element {
  return (
    <ul className={styles.legend} aria-label={label}>
      {entries.map((entry) => (
        <li key={entry.key} className={styles.legendStatic}>
          {entry.icon && (
            <span className={styles.legendIcon} aria-hidden="true">
              {entry.icon}
            </span>
          )}
          {/* The count on hover only: the legend explains the marks, it is not a tally */}
          <span title={entry.count !== undefined ? `${entry.label}: ${entry.count}` : undefined}>{entry.label}</span>
        </li>
      ))}
    </ul>
  );
}

const GLYPH_BOX = 30;

/**
 * A hero glyph at the drawing's scale, centred on its line of text: the glyphs stand on a baseline
 * (a balise is a dot at the foot, a signal is tall), so the box is moved to the middle of the drawing.
 */
export function FitGlyph({ symbol, size = 18 }: { symbol: string; size?: number }): JSX.Element {
  const ref = useRef<SVGGElement>(null);
  const [middle, setMiddle] = useState(GLYPH_BOX / 2);
  useLayoutEffect(() => {
    const group = ref.current;
    if (!group || typeof group.getBBox !== 'function') return;
    const box = group.getBBox();
    if (box.height > 0) setMiddle(box.y + box.height / 2);
  }, [symbol]);
  return (
    <svg className={styles.fitGlyph} width={size} height={size} viewBox={`0 ${middle - GLYPH_BOX / 2} ${GLYPH_BOX} ${GLYPH_BOX}`} aria-hidden="true">
      <g ref={ref}>
        <Glyph symbol={symbol} x={GLYPH_BOX / 2} y={GLYPH_BOX - 1} />
      </g>
    </svg>
  );
}
